import { EmptyState, IconSet, Panel } from '@postscript/components';
import { logAnnouncementDismissed } from 'components/admin/Announcements/eventUtil';
import { AnnouncementType } from 'components/admin/Announcements/utils';
import { useBanners } from 'components/GlobalBanners/globalBanners';
import { useLegacyNotifications } from 'controllers/contexts/legacyGlobalNotifications';
import { uniqBy } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { COMMON_EVENT_NAMES, logEvent, PRODUCT_AREAS } from 'utils/events';
import NotificationComponent from './NotificationComponent';
import {
  convertGlobalBannerToNotification,
  convertLegacyBannerToNotification,
} from './notificationUtils';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const StyledNotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
`;

const StyledEmptyState = styled(EmptyState)`
  padding: var(--spacing-10) 0;
`;

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const { banners, removeBanner } = useBanners();
  const {
    notifications: legacyNotifications,
    removeNotification: removeLegacyNotification,
  } = useLegacyNotifications();
  const previousIsOpenRef = useRef(isOpen);
  const { pathname } = useLocation();
  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    if (isOpen && pathname !== previousPathnameRef.current) {
      onClose();
    }
    previousPathnameRef.current = pathname;
  }, [isOpen, pathname, onClose]);

  const filteredLegacyNotifications = legacyNotifications.filter(
    (notification) =>
      !notification.data.announcementType ||
      notification.data.announcementType === AnnouncementType.APP_WIDE,
  );

  const filteredBanners = banners.filter(
    (banner) => banner?.announcementType !== AnnouncementType.EMERGENCY,
  );

  const totalNotifications =
    uniqBy(filteredBanners, 'id').length +
    uniqBy(filteredLegacyNotifications, 'id').length;
  const hasNotifications = totalNotifications > 0;

  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      logEvent('notifications panel opened', {
        location: 'nav menu',
        notification_count: totalNotifications,
        product_area: PRODUCT_AREAS.GLOBAL,
      });
    } else if (!isOpen && previousIsOpenRef.current) {
      logEvent('notifications panel closed', {
        location: 'nav menu',
        product_area: PRODUCT_AREAS.GLOBAL,
      });
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen, totalNotifications]);

  const globalNotifications = uniqBy(filteredBanners, 'id').map((banner) => {
    const notification = convertGlobalBannerToNotification(banner);
    const { primaryAction } = notification;
    return {
      ...notification,
      primaryAction:
        primaryAction && banner.closesPanelOnPrimaryAction
          ? {
              ...primaryAction,
              onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                onClose();
                primaryAction.onClick?.(event);
              },
            }
          : primaryAction,
      onDismiss: () => {
        if (banner.shouldPersistDismiss) {
          localStorage.setItem(
            `${banner.id}-banner-dismissed`,
            'user-dismissed',
          );
        }

        if (banner.isDismissable) {
          removeBanner(banner.id);
        }

        if (banner.data.onDismiss) {
          banner.data.onDismiss();
        }

        if (banner.eventTrackingOptions?.bannerType) {
          logEvent(COMMON_EVENT_NAMES.BANNER_DISMISSED, {
            banner_type: banner.eventTrackingOptions.bannerType,
          });
        }
      },
    };
  });

  const transformedLegacyNotifications = uniqBy(
    filteredLegacyNotifications,
    'id',
  ).map((notification) => ({
    ...convertLegacyBannerToNotification(notification),
    onDismiss: () => {
      localStorage.setItem(
        `announcement-${notification.id}-dismissed`,
        'user-dismissed',
      );

      logAnnouncementDismissed({
        bannerContent: notification.data.message || notification.message,
        id: notification.id,
      });
      removeLegacyNotification(notification.id);
    },
  }));

  const allNotifications = [
    ...globalNotifications,
    ...transformedLegacyNotifications,
  ];

  return (
    <Panel
      isOpen={isOpen}
      onCancel={onClose}
      placement="drawer"
      dismissOnBackdropClick
      visibleBackdrop={false}
      width={444}
    >
      <Panel.Header
        onCancel={onClose}
        icon={IconSet.Bell}
        iconBackgroundColor="var(--blue-1)"
      >
        Notifications
      </Panel.Header>
      <Panel.Body>
        {hasNotifications ? (
          <StyledNotificationsList>
            {allNotifications.map((notification) => (
              <NotificationComponent key={notification.id} {...notification} />
            ))}
          </StyledNotificationsList>
        ) : (
          <StyledEmptyState
            description="No notifications available"
            size="small"
            primaryAction={{
              'data-cy': 'notifications-panel-close',
              text: 'Close',
              onClick: onClose,
            }}
          />
        )}
      </Panel.Body>
    </Panel>
  );
};

export default NotificationsPanel;
